/**
 * Profile Context - Manages multi-profile/brand state
 * Each user can have multiple profiles (brands) with their own:
 * - Brand learning
 * - Social accounts
 * - Content
 * - Headshot/Logo
 */

import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { apiClient } from "./api";
import { toast } from "sonner";

const ProfileContext = createContext(null);

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error("useProfile must be used within ProfileProvider");
  }
  return context;
};

export const ProfileProvider = ({ children, user }) => {
  const [profiles, setProfiles] = useState([]);
  const [activeProfile, setActiveProfile] = useState(null);
  const [profileLimit, setProfileLimit] = useState(1);
  const [loading, setLoading] = useState(true);

  // Fetch user's profiles
  const fetchProfiles = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await apiClient.get("/users/profiles");
      const profileList = response.data.profiles || [];
      setProfiles(profileList);
      setProfileLimit(response.data.profile_limit || 1);
      
      // Set active profile based on API's active_profile_id (source of truth)
      const serverActiveId = response.data.active_profile_id;
      const activeFromServer = profileList.find(p => p.id === serverActiveId);
      const activeFromIsDefault = profileList.find(p => p.is_default);
      
      if (activeFromServer) {
        setActiveProfile(activeFromServer);
        localStorage.setItem("viraly_active_profile", activeFromServer.id);
      } else if (activeFromIsDefault) {
        setActiveProfile(activeFromIsDefault);
        localStorage.setItem("viraly_active_profile", activeFromIsDefault.id);
      } else if (profileList.length > 0) {
        // Fallback to first profile
        setActiveProfile(profileList[0]);
        localStorage.setItem("viraly_active_profile", profileList[0].id);
      }
    } catch (error) {
      console.error("Failed to fetch profiles:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  // Switch active profile
  const switchProfile = async (profileId) => {
    const profile = profiles.find(p => p.id === profileId);
    if (!profile) return;
    
    try {
      await apiClient.post(`/users/profiles/${profileId}/activate`);
      setActiveProfile(profile);
      localStorage.setItem("viraly_active_profile", profileId);
      
      // Update profiles array to reflect new is_default state
      setProfiles(prevProfiles => 
        prevProfiles.map(p => ({
          ...p,
          is_default: p.id === profileId
        }))
      );
      
      toast.success(`Switched to ${profile.name}`);
    } catch (error) {
      toast.error("Failed to switch profile");
    }
  };

  // Create new profile
  const createProfile = async (name, niche, description) => {
    if (profiles.length >= profileLimit) {
      toast.error(`Profile limit reached. Your plan allows ${profileLimit} profile(s).`);
      return null;
    }
    
    try {
      const response = await apiClient.post("/users/profiles", {
        name,
        niche,
        description
      });
      
      const newProfile = response.data.profile;
      setProfiles(prev => [...prev, newProfile]);
      toast.success(`Profile "${name}" created!`);
      return newProfile;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create profile");
      return null;
    }
  };

  // Update profile
  const updateProfile = async (profileId, updates) => {
    try {
      const response = await apiClient.put(`/users/profiles/${profileId}`, updates);
      const updated = response.data.profile;
      
      setProfiles(prev => prev.map(p => p.id === profileId ? updated : p));
      if (activeProfile?.id === profileId) {
        setActiveProfile(updated);
      }
      
      toast.success("Profile updated");
      return updated;
    } catch (error) {
      toast.error("Failed to update profile");
      return null;
    }
  };

  // Delete profile
  const deleteProfile = async (profileId) => {
    const profile = profiles.find(p => p.id === profileId);
    if (!profile) return false;
    
    if (profile.is_default) {
      toast.error("Cannot delete default profile");
      return false;
    }
    
    try {
      await apiClient.delete(`/users/profiles/${profileId}`);
      setProfiles(prev => prev.filter(p => p.id !== profileId));
      
      // If deleted active profile, switch to default
      if (activeProfile?.id === profileId) {
        const defaultProfile = profiles.find(p => p.is_default);
        if (defaultProfile) {
          switchProfile(defaultProfile.id);
        }
      }
      
      toast.success(`Profile "${profile.name}" deleted`);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete profile");
      return false;
    }
  };

  const value = {
    profiles,
    activeProfile,
    profileLimit,
    loading,
    canCreateMore: profiles.length < profileLimit,
    profilesUsed: profiles.length,
    switchProfile,
    createProfile,
    updateProfile,
    deleteProfile,
    refreshProfiles: fetchProfiles
  };

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
};

export default ProfileContext;
